/**
 * Map of human-readable modifier names to their flag values
 */
export const modifiers: { [modifier: string]: number } = {
    "shift"  : 0x10000,
    "alt"    : 0x20000,
    "option" : 0x20000, // alias for alt
    "ctrl"   : 0x40000,
    "control": 0x40000, // alias for ctrl
    "meta"   : 0x80000,
    "command": 0x80000, // alias for meta
    "win"    : 0x80000  // alias for meta
};

/**
 * Map of human-readable names of known key names to their ASCII values
 */
export const codes: { [code: string]: number } = {
    "backspace": 8,   // 0x08
    "tab"      : 9,   // 0x09
    "enter"    : 13,  // 0x0D
    "sleep"    : 19,  // 0x13
    "escape"   : 27,  // 0x1B
    "esc"      : 27,
    "space"    : 32,  // 0x20
    "pageup"   : 33,  // 0x21
    "pagedown" : 34,  // 0x22
    "end"      : 35,  // 0x23
    "home"     : 36,  // 0x24
    "left"     : 37,  // 0x25
    "up"       : 38,  // 0x26
    "right"    : 39,  // 0x27
    "down"     : 40,  // 0x28
    "insert"   : 45,  // 0x2D
    "delete"   : 46,  // 0x2E
    "0"        : 48,  // 0x30
    "1"        : 49,  // 0x31
    "2"        : 50,  // 0x32
    "3"        : 51,  // 0x33
    "4"        : 52,  // 0x34
    "5"        : 53,  // 0x35
    "6"        : 54,  // 0x36
    "7"        : 55,  // 0x37
    "8"        : 56,  // 0x38
    "9"        : 57,  // 0x39
    "a"        : 65,  // 0x41
    "b"        : 66,  // 0x42
    "c"        : 67,  // 0x43
    "d"        : 68,  // 0x44
    "e"        : 69,  // 0x45
    "f"        : 70,  // 0x46
    "g"        : 71,  // 0x47
    "h"        : 72,  // 0x48
    "i"        : 73,  // 0x49
    "j"        : 74,  // 0x4A
    "k"        : 75,  // 0x4B
    "l"        : 76,  // 0x4C
    "m"        : 77,  // 0x4D
    "n"        : 78,  // 0x4E
    "o"        : 79,  // 0x4F
    "p"        : 80,  // 0x50
    "q"        : 81,  // 0x51
    "r"        : 82,  // 0x52
    "s"        : 83,  // 0x53
    "t"        : 84,  // 0x54
    "u"        : 85,  // 0x55
    "v"        : 86,  // 0x56
    "w"        : 87,  // 0x57
    "x"        : 88,  // 0x58
    "y"        : 89,  // 0x59
    "z"        : 90,  // 0x5A
    "numpad0"  : 96,  // 0x60
    "numpad1"  : 97,  // 0x61
    "numpad2"  : 98,  // 0x62
    "numpad3"  : 99,  // 0x63
    "numpad4"  : 100, // 0x64
    "numpad5"  : 101, // 0x65
    "numpad6"  : 102, // 0x66
    "numpad7"  : 103, // 0x67
    "numpad8"  : 104, // 0x68
    "numpad9"  : 105, // 0x69
    "numpad*"  : 106, // 0x6A
    "numpad+"  : 107, // 0x6B
    "numpad-"  : 109, // 0x6D
    "numpad."  : 110, // 0x6E
    "numpad/"  : 111, // 0x6F
    "f1"       : 112, // 0x70
    "f2"       : 113, // 0x71
    "f3"       : 114, // 0x72
    "f4"       : 115, // 0x73
    "f5"       : 116, // 0x74
    "f6"       : 117, // 0x75
    "f7"       : 118, // 0x76
    "f8"       : 119, // 0x77
    "f9"       : 120, // 0x78
    "f10"      : 121, // 0x79
    "f11"      : 122, // 0x7A
    "f12"      : 123, // 0x7B
    "<"        : 188, // 0xBC
    ","        : 188,
    ">"        : 190, // 0xBE
    "."        : 190,
    "["        : 219, // 0xDB
    "{"        : 219,
    "]"        : 221, // 0xDD
    "}"        : 221
};

/**
 * Maps key hash to action.
 */
export interface KeyHashMap<TAction> {
    [shortcut: number]: TAction;
}

/**
 * Schema of symbolic name to key shortcut.
 */
export interface Schema {
    [symbolicName: string]: string;
}

/**
 * Maps shortcut to action.
 */
export interface ShortcutMap<TAction> {
    [shortcut: string]: TAction;
}

/**
 * Compute a numeric hash code (see modifiers)
 * from shift, alt and ctrl keys.
 */
export function calculateHash(event: JQueryKeyEventObject): number {
    return (event.which
        + (event.shiftKey ? modifiers["shift"] : 0)
        + (event.altKey ? modifiers["alt"] : 0)
        + (event.ctrlKey ? modifiers["ctrl"] : 0));
}

/**
 * TAction is usually a JQuery key event handler when
 * used by pli/keys, or a string when used by ko-grid,
 * but it is up to the caller to provide the action type.
 */
export default class KeyMap<TAction> { // tslint:disable-line: no-default-export // TODO: remove in v8
    constructor(shortcutMap: ShortcutMap<TAction>, id?: string, schema?: Schema) {
        this.id = id;
        this.shortcutMap = shortcutMap;
        this.schema = schema || {};
    }

    /**
     * Translate keymap handlers map (this.shortcutMap)
     * into lookup map (this.lookup).
     */
    createLookup(): void {
        let k: string,
            h: number[],
            l: KeyHashMap<TAction>;

        if (this.lookup === null) {
            this.lookup = l = { };
            for (k in this.shortcutMap) {
                if (this.shortcutMap.hasOwnProperty(k)) {
                    h = KeyMap.parseShortcuts(k, this.schema);
                    for (let i = 0; i < h.length; i += 1) {
                        if (l.hasOwnProperty(<any>h[i])) {
                            throw new Error(`Multiple handlers for the same shortcut (0x${h[i].toString(16)}) are not allowed`);
                        }
                        l[h[i]] = this.shortcutMap[k];
                    }
                }
            }
        }
    }

    /**
     * Parse the specified shortcuts string into
     * an array of hashes (each hash will be a number
     * that uniquely identifies a key-combination
     * that could arrive in onkeydown event).
     * References to schema entries (if a schema
     * was set) will be resolved recursively
     * (i.e. with schema { actionA: "actionB", actionB: "ctrl+B" }
     * parsing "actionA" will return hash for "ctrl+B").
     */
    static parseShortcuts(value: string, schema?: Schema): number[] {
        const h: number[] = [];
        const v = (value && value.toString().replace(/\s/g, "").toLowerCase()) || null;
        KeyMap.parseSymbolicNames(v, v, h, schema || {});
        return h;
    }

    /**
     * Updates keymap's schema and resets lookup to null.
     * This will cause handler maps to be re-translated into lookup maps when needed.
     */
    setSchema(schema: Schema): void {
        this.lookup = null;
        this.schema = schema;
    }

    /**
     * Parse one shortcut into a hash value
     * (return value). Will throw Error(s)
     * if unable to determine the hash value.
     */
    private static parseShortcut(value: string, original: string): number {
        const shortcutParts = value.split("+");
        let shortcutPart: string,
            hash = 0,
            codeFound = false,
            h: number;

        for (let i = 0; i < shortcutParts.length; i += 1) {
            shortcutPart = shortcutParts[i];
            if (shortcutPart) {
                h = modifiers[shortcutPart];
                if (h) {
                    hash |= h;
                } else {
                    h = codes[shortcutPart];
                    if (h) {
                        if (codeFound) {
                            throw new Error("Shortcut \"".concat(value, "\" (original \"", original, "\") specifies more than one key name"));
                        }
                        codeFound = true;
                        hash |= h;
                    } else {
                        throw new Error("Shortcut part \"".concat(shortcutPart, "\" in \"", value, "\" (original \"", original, "\") is neither modifier nor key nor schema name"));
                    }
                }
            }
        }
        if (!codeFound) {
            throw new Error("Shortcut \"".concat(value, "\" (original \"", original, "\") did not specify a key name"));
        }
        return hash;
    }

    /**
     * Recursively translate all parts of a shortcuts string
     * into hashes by first resolving schema (symbolic)
     * names into shortcuts and then translating shortcuts
     * into hash values (using parseShortcut).
     */
    private static parseSymbolicNames(value: string, original: string, hashArray: number[], schema: Schema): void {
        let symbolicNames: string[],
            symbolicName: string,
            translation: string;

        if (value) {
            symbolicNames = value.split(",");
            for (let i = 0; i < symbolicNames.length; i += 1) {
                symbolicName = symbolicNames[i];
                if (symbolicName) { // notice that we"re ignoring empty entries, e.g. if value looked like ",  ,, ,something, ,", only "something" would be processed
                    translation = schema[symbolicName];
                    if (translation) {
                        this.parseSymbolicNames(translation.toString().replace(/\s/g, "").toLowerCase(), original, hashArray, schema); // recursive call
                    } else { // no translation - symbolic name is a shortcut, let"s parse it now
                        hashArray.push(this.parseShortcut(symbolicName, original));
                    }
                }
            }
        }
    }

    /**
     * ID that uniquely identifies this keymap,
     * as specified in constructor.
     */
    public id: string;

    /**
     * Translated version of keymap, i.e. a map
     * containing hash values as keys and handlers
     * as values
     */
    public lookup: KeyHashMap<TAction> = null;

    private schema: Schema;
    private shortcutMap: ShortcutMap<TAction>;
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/util/keyMap.ts